import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { NavLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalShippingTwoTone from "@mui/icons-material/LocalShippingTwoTone";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";

import {
  InsertDriveFileOutlined,
  SettingsOutlined,
  PersonAddOutlined,
  DriveFileMoveOutlined,
  TableRowsOutlined,
  AssignmentOutlined,
  ContentPaste,
} from "@mui/icons-material";
import { useTablePagination } from "@context/table-pagination-context";
import { useSidebar } from "@context/sidebar-context";
import { useIsTablet } from "../../hook/use-is-tablet";
import {
  canManageUsers,
  canViewAdministration,
  canViewLiteCustoms,
  canViewLiteDelivery,
  canViewLiteDeliveryWhs,
  canViewLiteTms,
  canViewLiteTmsWhs,
  canViewLiteWms,
  canViewOfferList,
} from "@helpers/permission";

type SideMenuItem = {
  text: string;
  icon: React.ReactNode;
  to: string;
} & (
  | { role: string[]; canAccess?: never }
  | { canAccess: (role: string) => boolean; role?: never }
);

const canAccessSideMenuItem = (item: SideMenuItem, role: string): boolean =>
  "canAccess" in item && item.canAccess
    ? item.canAccess(role)
    : item.role.includes(role);

const listItems: SideMenuItem[] = [
  {
    text: "Users",
    icon: <PersonAddOutlined />,
    to: "/users",
    canAccess: canManageUsers,
  },
  {
    text: "Offers",
    icon: <InsertDriveFileOutlined />,
    to: "/offers",
    canAccess: canViewOfferList,
  },
  {
    text: "Shipping Cases",
    icon: <DriveFileMoveOutlined />,
    to: "/shippings",
    role: ["Administration", "Sales", "Operations", "Customer"],
  },
  {
    text: "Lite WMS",
    icon: <TableRowsOutlined />,
    to: "/wms",
    canAccess: canViewLiteWms,
  },
  {
    text: "Lite TMS",
    icon: <LocalShippingOutlinedIcon />,
    to: "/tms",
    canAccess: canViewLiteTms,
  },
  {
    text: "Lite TMS (WHS)",
    icon: <LocalShippingTwoTone />,
    to: "/tms-whs",
    canAccess: canViewLiteTmsWhs,
  },
  {
    text: "Lite Delivery Orders",
    icon: <AssignmentOutlined />,
    to: "/delivery",
    canAccess: canViewLiteDelivery,
  },
  {
    text: "Lite Delivery Orders (WHS)",
    icon: <ContentPaste />,
    to: "/whs-delivery",
    canAccess: canViewLiteDeliveryWhs,
  },
  {
    text: "Lite Customs Bill",
    icon: <FactCheckOutlinedIcon />,
    to: "/customs",
    canAccess: canViewLiteCustoms,
  },
  {
    text: "Administration",
    icon: <SettingsOutlined />,
    to: "/settings",
    canAccess: canViewAdministration,
  },
];

const SideMenu: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const { isOpen } = useSidebar();
  const isTablet = useIsTablet();

  const { resetAll } = useTablePagination();

  const filteredItems = isTablet
    ? listItems.filter(
        (item) =>
          item.to === "/wms" || item.to === "/tms" || item.to === "/tms-whs",
      )
    : listItems;

  const isActive = (itemPath: string): boolean => {
    const shippingCaseMatch =
      /^\/offers\/\d+\/shipping-case\/(create|\d+)(?:\/.*)?$/.test(currentPath);

    if (shippingCaseMatch && itemPath === "/shippings") return true;
    if (shippingCaseMatch && itemPath === "/offers") return false;

    return (
      currentPath === itemPath ||
      currentPath.startsWith(`${itemPath}/`) ||
      currentPath.startsWith(`/${itemPath}/`)
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: isOpen ? "256px" : "80px",
        boxShadow: "1px 0 3px rgba(0, 0, 0, 0.1)",
        transition: "width 0.3s ease, min-width 0.3s ease",
        overflow: "visible",
        flexShrink: 0,
      }}
    >
      <Stack
        sx={{
          p: 1,
          top: 0,
          width: isOpen ? "256px" : "80px",
          transition: "width 0.3s ease, min-width 0.3s ease",
          flexGrow: 1,
          position: "sticky",
          justifyContent: "space-between",
          maxHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <List dense component="nav">
          {filteredItems.map((item, index) => {
            if (role && canAccessSideMenuItem(item, role))
              return (
                <Tooltip
                  key={index}
                  title={!isOpen ? item.text : ""}
                  placement="right"
                  arrow
                >
                  <ListItem
                    component={NavLink}
                    to={item.to}
                    disablePadding
                    onClick={() => resetAll()}
                    sx={{ display: "block", color: "black" }}
                  >
                    <ListItemButton
                      selected={isActive(item.to)}
                      sx={{
                        minHeight: 48,
                        justifyContent: isOpen ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isOpen ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {
                        <ListItemText
                          primary={item.text}
                          sx={{
                            whiteSpace: "nowrap",
                            opacity: isOpen ? 1 : 0,
                            width: isOpen ? "100%" : 0,
                            transition: "width 0.2s ease",
                            transitionDelay: isOpen ? "0.3s" : "0s",
                          }}
                        />
                      }
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              );
          })}
        </List>
      </Stack>
    </Box>
  );
};

export default SideMenu;
